import {
  Languages,
  Mic,
  BookOpenCheck,
  ListChecks,
  GraduationCap,
  FlaskConical,
} from "lucide-react";

export const products = {
  k12: {
    slug: "k12",
    name: "CognitLoop K-12",
    navLabel: "K-12",
    tagline: "Curriculum-grounded one-to-one tutor for school students",
    bannerTitle: "CognitLoop K-12 — curriculum-grounded school tutor",
    category: "K-12 · US / Canada / UK",
    accent: "blue",
    theme: {
      text: "text-blue-700",
      border: "border-blue-200",
      badgeBg: "bg-blue-50",
      softBg: "bg-blue-50/60",
      ring: "focus-visible:ring-blue-500",
      cta: "bg-blue-600 hover:bg-blue-700",
      gradient: "from-blue-500 to-indigo-500",
      glow: "group-hover:shadow-[0_18px_50px_-12px_rgba(59,130,246,0.35)]",
      dot: "bg-blue-500",
      iconBg: "bg-blue-100 text-blue-700",
    },
    shortDescription:
      "Standards-aligned one-to-one tutoring for school students in the US, Canada, and the UK. The same agentic mastery loop — mapped to your curriculum — so every student builds on solid ground.",
    mission:
      "Most AI tutors for school-age kids ignore the curriculum and wing it. CognitLoop K-12 stays on syllabus for students in the US, Canada, and the UK: it maps to Common Core, AP, IB, and major board standards, builds a personalised learning path for each student, and runs the same sense-predict-correct loop that drives every CognitLoop product. No hallucinated lessons, no skipped foundations.",
    audience:
      "K-12 students in the US, Canada, and the UK — and the parents and teachers who support them.",
    comingSoon: true,
    comingSoonLabel: "Beta November 2026",
    cta: { label: "Request beta access", href: "/#newsletter" },
    image: "/k12-tutor.png",
    features: [
      {
        icon: GraduationCap,
        title: "Standards-locked, not standards-adjacent",
        description:
          "Every lesson is mapped to Common Core, AP, IB, or your board curriculum. The agent knows what grade-level mastery looks like and won't advance until the student can demonstrate it.",
      },
      {
        icon: ListChecks,
        title: "Personalised path from day one",
        description:
          "A short diagnostic at onboarding locates gaps in prior knowledge. The plan fills those gaps first, then advances — so no student is building on shaky foundations.",
      },
      {
        icon: FlaskConical,
        title: "Subjects across the full curriculum",
        description:
          "Maths, science, English, history, and more. One tutor that adapts its approach by subject — step-by-step for algebra, evidence-based for essay writing.",
      },
    ],
  },

  padhlo: {
    slug: "padhlo",
    name: "Padhlo",
    navLabel: "Padhlo",
    tagline: "India K-12 tutor, in the mother tongue",
    bannerTitle: "Padhlo — India K-12 tutor, in the mother tongue",
    category: "K-12 · India",
    accent: "emerald",
    theme: {
      text: "text-emerald-700",
      border: "border-emerald-200",
      badgeBg: "bg-emerald-50",
      softBg: "bg-emerald-50/60",
      ring: "focus-visible:ring-emerald-500",
      cta: "bg-emerald-600 hover:bg-emerald-700",
      gradient: "from-emerald-500 to-teal-500",
      glow: "group-hover:shadow-[0_18px_50px_-12px_rgba(16,185,129,0.35)]",
      dot: "bg-emerald-500",
      iconBg: "bg-emerald-100 text-emerald-700",
    },
    shortDescription:
      "The same one-to-one mastery loop for Indian school students — NCERT and board syllabi, taught in the language they actually think in.",
    mission:
      "A child's language should never cap what they can learn. Padhlo is a one-to-one tutor for India: voice-first, grounded in NCERT and regional board syllabi, with the same plan-adapt-master loop that drives every CognitLoop product. If you think in Hindi, Tamil, Telugu, or any of 12+ supported languages, Padhlo teaches in that language — not a translation, a native explanation.",
    audience:
      "Indian students studying under NCERT and regional board syllabi, from primary school through Class 12.",
    comingSoon: true,
    comingSoonLabel: "Beta November 2026",
    cta: { label: "Visit Padhlo", href: "https://padhlo.com" },
    image: "/padhlo-tutor.png",
    features: [
      {
        icon: Languages,
        title: "Mother-tongue instruction",
        description:
          "Real-time teaching across 12+ Indian languages so complex ideas land in the language the student thinks in — not a translated afterthought.",
      },
      {
        icon: BookOpenCheck,
        title: "NCERT and board grounding",
        description:
          "Every lesson stays on syllabus. NCERT, CBSE, ICSE, and major state boards. No off-board drift, no hallucinated curriculum chapters.",
      },
      {
        icon: Mic,
        title: "Voice-first, low bandwidth",
        description:
          "Built for spoken interaction and constrained networks — works on the devices most Indian students actually have, not just a high-spec desktop.",
      },
    ],
  },
};

export const productList = [products.k12, products.padhlo];
